import { FC, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Global, css as emotionCSS } from '@emotion/react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
} from '@mildang/mui';
import Eye from '@mildang/icons/react/eye';
import EyeOff from '@mildang/icons/react/eye-off';
import TextFieldHook from '@mildang/design-system-legacy/esm/components/molecules/TextField/TextFieldHook';
import { debouncePhoneNumber } from '@shared/utils/validate/phoneNumber';
import { toast } from '@ui/components/molecules/Toaster';
import { SendAuthVerifySmsDocument, SendAuthVerifySmsMutation, SendAuthVerifySmsMutationVariables } from '@ui/mutations/sendAuthVerifySMS';
import useVerifyAuthCode from '@ui/components/user/patterns/FindEmailDialog/hooks/useVerifyAuthCode';
import PhoneNumberFormat from '@ui/components/user/patterns/FindEmailDialog/PhoneNumberFormat';
import { ResetPasswordWithVerificationCodeDocument, ResetPasswordWithVerificationCodeMutation, ResetPasswordWithVerificationCodeMutationVariables } from '@ui/mutations/resetPasswordWithVerificationCode/resetPasswordWithVerificationCode.generated';
import useWebOTPInput from '@ui/hooks/useWebOTPInput';
import {
  textFieldWrapperStyle,
  dialogTitleStyle,
  formStyle,
  fieldsetStyle,
  dialogContentTextStyle,
  dialogActionsStyle,
  globalStyle,
} from './style';
import { ApolloError, useMutation } from '@apollo/client';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

// student-web에서 쓰던 파서처럼 서버 메시지를 먼저 보여준다.
const errorMessage = (error: unknown) =>
  (error as ApolloError)?.graphQLErrors?.[0]?.message ||
  '알 수 없는 에러로 인해 요청이 정상적으로 처리되지 않았습니다.';

const schema = (isVerified: boolean) =>
  yup.object().shape({
    email: yup.string().email('올바르지 않은 이메일 입니다.').required('이메일은 필수값 입니다.'),
    phoneNumber: yup
      .string()
      .required('전화번호는 필수값 입니다.')
      .test('is-phoneNumber', '올바른 핸드폰 번호가 아닙니다.', async (value) => debouncePhoneNumber(value)),
    phoneVerifyCode: yup
      .string()
      .required('인증번호는 필수값 입니다.')
      .length(4, '인증번호 4자리를 입력해주세요'),
    ...(isVerified && {
      newPassword: yup
        .string()
        // TODO: use utils
        .matches(/^(?=.*[a-zA-Z])(?=.*[0-9]).{6,16}$/, '6~ 16자리(영문, 숫자가 모두 포함)')
        .required('새로운 비밀번호를 작성해주세요.'),
    }),
  });

const showTextField = (hide?: boolean) => emotionCSS`
  ${
    hide &&
    emotionCSS`
    display: none;
  `
  }
`;

interface IValues {
  email: string;
  phoneNumber: string;
  phoneVerifyCode: string;
  newPassword: string;
}

export interface ResetPasswordDialogProps {
  open: boolean;
  onClose?: () => void;
  onSubmitProp?: (formData: { email: string; phoneNumber: string }) => void;
  onSubmitError?: (e: unknown) => void;
}

// B2C 로그인과 LMS·CMS·CRM 공용 로그인 화면이 함께 쓴다. 오류 표시와 분석 로그는 호출하는 쪽이 정한다.
const ResetPasswordDialog: FC<ResetPasswordDialogProps> = ({ open, onClose, onSubmitProp, onSubmitError }) => {
  const { _ } = useLingui();

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const [sendAuthVerifySms, sendAuthVerifySMSResult] = useMutation<SendAuthVerifySmsMutation, SendAuthVerifySmsMutationVariables>(SendAuthVerifySmsDocument, {});
  const {
    verifyAuthCode,
    called: isVerifyCalled,
    data: verifyResult,
    loading: verifyAuthCodeLoading,
  } = useVerifyAuthCode();
  const isVerified = isVerifyCalled && !!verifyResult?.verifyAuthCode?.isVerified;

  const [resetPassword] = useMutation<ResetPasswordWithVerificationCodeMutation, ResetPasswordWithVerificationCodeMutationVariables>(ResetPasswordWithVerificationCodeDocument, {});

  const resolvedSchema = useMemo(() => yupResolver(schema(isVerified)), [isVerified]);

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    clearErrors,
    watch,
    setValue,
    getValues,
    control,
  } = useForm<IValues>({
    resolver: resolvedSchema,
    mode: 'onChange',
    defaultValues: {
      email: '',
      phoneNumber: '',
      phoneVerifyCode: '',
      newPassword: '',
    },
  });
  const email = watch('email');
  const { reset } = sendAuthVerifySMSResult;
  useEffect(() => {
    if (email) {
      reset();
    }
  }, [reset, email]);

  const handleReceiveCode = useCallback(
    (code: string) => {
      setValue('phoneVerifyCode', code, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setTimeout(() => {
        submitButtonRef?.current?.click?.();
      }, 10);
    },
    [setValue],
  );

  const { listenOTP, abortListen: abortSMSListen } = useWebOTPInput({
    onReceiveCode: handleReceiveCode,
  });

  const handleClickVerifyCode = useCallback(async () => {
    const phoneNumber = getValues('phoneNumber');
    try {
      await sendAuthVerifySms({
        variables: {
          input: {
            type: 'FIND_PASSWORD',
            phoneNumber: phoneNumber,
          },
        },
      });
      clearErrors('phoneNumber');
    } catch (e) {
      abortSMSListen();
      setError('phoneNumber', {
        message: errorMessage(e),
      });
    }

    await listenOTP();
  }, [getValues, sendAuthVerifySms, listenOTP, abortSMSListen, setError, clearErrors]);

  const onSubmit = handleSubmit(async (formData) => {
    try {
      if (!verifyResult?.verifyAuthCode?.isVerified) {
        try {
          const result = await verifyAuthCode({
            variables: {
              input: {
                code: Number(formData.phoneVerifyCode),
                phoneNumber: formData.phoneNumber,
              },
            },
          });

          if (!result.data?.verifyAuthCode.isVerified) {
            setError('phoneVerifyCode', {
              message: '인증이 실패하였습니다.',
            });
          }
        } catch (error) {
          setError('phoneVerifyCode', {
            message: errorMessage(error),
            // message: '인증이 실패하였습니다.',
          });
        }

        return;
      }

      onSubmitProp?.({ email: formData.email, phoneNumber: formData.phoneNumber });
      await resetPassword({
        variables: {
          input: {
            code: Number(formData.phoneVerifyCode),
            phoneNumber: formData.phoneNumber,
            email: formData.email,
            newPassword: formData.newPassword,
          },
        },
      });

      toast('비밀번호가 변경되었습니다.');
      onClose?.();
    } catch (e) {
      onSubmitError?.(e);
    }
  });

  return (
    <>
      <Global styles={globalStyle} />
      <Dialog
        open={open}
        fullWidth
        onClose={onClose}
        aria-labelledby="find-account-dialog-title"
        PaperProps={{
          className: 'LoginScreen_Dialog_Paper',
        }}
      >
        <form css={formStyle} noValidate onSubmit={onSubmit}>
          <DialogTitle css={dialogTitleStyle} id="find-account-dialog-title">
            비밀번호 재설정하기
          </DialogTitle>

          <div css={fieldsetStyle}>
            <DialogContentText css={dialogContentTextStyle}>
              {isVerified
                ? '새로운 비밀번호를 입력해주세요.'
                : '가입시 사용한 이메일과 연락처를 입력해주세요.'}
            </DialogContentText>
            <TextFieldHook
              css={[textFieldWrapperStyle, showTextField(isVerified)]}
              control={control}
              required
              type="email"
              name="email"
              label="이메일"
              placeholder="이메일을 입력해주세요"
              fullWidth
              disabled={verifyAuthCodeLoading}
            />
            <TextFieldHook
              css={[textFieldWrapperStyle, showTextField(isVerified)]}
              control={control}
              required
              name="phoneNumber"
              label="전화번호"
              placeholder="전화번호를 입력해주세요"
              disabled={verifyAuthCodeLoading}
              InputProps={{
                inputComponent: PhoneNumberFormat,
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      data-ga-name="sendSms_btn"
                      size="small"
                      variant="outlined"
                      disabled={sendAuthVerifySMSResult.loading || verifyAuthCodeLoading || isSubmitting}
                      onClick={handleClickVerifyCode}
                    >
                      {sendAuthVerifySMSResult.called ? '재전송' : '인증번호 전송'}
                    </Button>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            {sendAuthVerifySMSResult.called && (
              <TextFieldHook
                css={[textFieldWrapperStyle, showTextField(isVerified)]}
                control={control}
                required
                name="phoneVerifyCode"
                label="인증번호"
                placeholder="4자리 인증번호를 입력해주세요"
                disabled={verifyAuthCodeLoading}
                inputProps={{
                  autoComplete: 'one-time-code',
                  inputMode: 'numeric',
                }}
                fullWidth
              />
            )}
            {isVerified && (
              <TextFieldHook
                css={[textFieldWrapperStyle, showTextField(!isVerified)]}
                control={control}
                required
                name="newPassword"
                label="새 비밀번호"
                helperText={errors.newPassword?.message ?? _(msg`6~16자리(영문, 숫자가 모두 포함)`)}
                placeholder="새 비밀번호를 입력해주세요"
                type={showPassword ? 'text' : 'password'}
                disabled={verifyAuthCodeLoading}
                inputProps={{
                  autoComplete: 'new-password',
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        tabIndex={-1}
                        aria-label="toggle password visibility"
                        onClick={toggleShowPassword}
                        edge="end"
                      >
                        {showPassword ? <Eye /> : <EyeOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            )}
          </div>

          <DialogActions css={dialogActionsStyle}>
            <Button disabled={isSubmitting || verifyAuthCodeLoading} onClick={onClose}>
              취소
            </Button>

            <Button
              ref={submitButtonRef}
              disabled={isSubmitting || verifyAuthCodeLoading}
              variant="contained"
              color="primary"
              type="submit"
            >
              {isVerified ? '비밀번호 변경하기' : '비밀번호 찾기'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ResetPasswordDialog;
